import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Bell, Clock, LogOut, Settings, User, X } from 'lucide-react'
import { Avatar } from './Avatar'
import { MenuIcon, SearchIcon } from './Icons'
import { SearchInput } from './SearchInput'
import { NotificationPanel } from './NotificationPanel'
import { useAuthStore } from '../features/auth/hooks/useAuthStore'
import { useOrganizationProfile } from '../shared/hooks/useOrganizationProfile'
import { LogoutConfirmModal } from './LogoutConfirmModal'
import {
  useNotifications,
  useUnreadCount,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '../hooks/useNotifications'
import { showToastMessage } from './Toast'

interface HeaderProps {
  onMenuClick?: () => void
  pageTitle?: string
  breadcrumb?: string[] | string
  userName?: string
  userRole?: string
  avatarInitials?: string
  avatarSrc?: string
  hideSidebar?: boolean
  hideBreadcrumb?: boolean
}

type SearchItem = {
  id: string
  title: string
  subtitle: string
  path: string
  type: string
  permission?: string
}

// Navigable Teacher Modules & Quick Actions
const teacherSearchItems: SearchItem[] = [
  { id: 'teacher-dashboard', title: 'Dashboard', subtitle: 'Overview dashboard and daily tools', path: '/teacher/dashboard', type: 'Main', permission: 'dashboard:view' },
  { id: 'teacher-myclasses', title: 'My Classes', subtitle: 'Manage your classes, students and timetable', path: '/teacher/myclasses', type: 'Classroom', permission: 'class-management:view' },
  { id: 'teacher-attendance', title: 'Attendance', subtitle: 'Mark and view student attendance', path: '/teacher/attendence', type: 'Classroom', permission: 'attendance:view' },
  { id: 'teacher-assignments', title: 'Assignments & Homework', subtitle: 'Create homework and review student submissions', path: '/teacher/assignments', type: 'Classroom', permission: 'assignments:view' },
  { id: 'teacher-assignments-create', title: 'Create Homework Assignment', subtitle: 'Post new homework or assignment to class', path: '/teacher/assignments/create', type: 'Action', permission: 'assignments:create' },
  { id: 'teacher-assignments-review', title: 'Review Submissions', subtitle: 'Evaluate student submitted homework', path: '/teacher/assignments/review', type: 'Action', permission: 'assignments:view' },
  { id: 'teacher-assignments-report', title: 'Assignment Report', subtitle: 'View submission progress reports', path: '/teacher/assignments/report', type: 'Report', permission: 'assignments:view' },
  { id: 'teacher-lessonplans', title: 'Lesson Plans', subtitle: 'Manage daily teaching lesson plans', path: '/teacher/lessonplans', type: 'Classroom', permission: 'lesson-plans:view' },
  { id: 'teacher-lessonplans-create', title: 'Create Lesson Plan', subtitle: 'Draft a new lesson plan', path: '/teacher/lessonplans/create', type: 'Action', permission: 'lesson-plans:create' },
  { id: 'teacher-examinations', title: 'Examinations & Marks', subtitle: 'Enter and review student exam marks', path: '/teacher/examinations', type: 'Academics', permission: 'examinations:view' },
  { id: 'teacher-student-performance', title: 'Student Performance', subtitle: 'Analyze student grades and analytics', path: '/teacher/student-performance', type: 'Academics', permission: 'examinations:view' },
  { id: 'teacher-leave-management', title: 'Apply Leave', subtitle: 'Request leaves and view leave history', path: '/teacher/leave-management', type: 'Academic', permission: 'leave:apply' },
  { id: 'teacher-communication', title: 'Communication', subtitle: 'View school-wide notices, announcements and updates', path: '/teacher/communication', type: 'Communication', permission: 'communication:view' },
  { id: 'teacher-library', title: 'Library', subtitle: 'Access the digital catalog and resources', path: '/teacher/library', type: 'Resources', permission: 'library:view' },
  { id: 'teacher-profile', title: 'Teacher Profile', subtitle: 'View and update profile information', path: '/teacher/profile', type: 'Profile', permission: 'profile:view' },
  { id: 'teacher-settings', title: 'Teacher Settings', subtitle: 'Account settings and password management', path: '/teacher/settings', type: 'Settings', permission: 'settings:view' },
]

// Navigable School Admin Modules & Quick Actions (100% Verified Routes)
const adminSearchItems: SearchItem[] = [
  // Main
  { id: 'admin-dashboard', title: 'Dashboard', subtitle: 'Overview analytics, stats, and school activities', path: '/admin/dashboard', type: 'Main', permission: 'dashboard:view' },

  // Front Office
  { id: 'admin-student-directory', title: 'Student Directory', subtitle: 'Search and manage student profiles', path: '/admin/student-directory', type: 'Front Office', permission: 'student-directory:view' },
  { id: 'admin-doc-verification', title: 'Document Verification', subtitle: 'Verify applicant documents', path: '/admin/document-verification', type: 'Front Office', permission: 'document-verification:view' },
  { id: 'admin-staff-directory', title: 'Staff Directory', subtitle: 'Manage teachers and school staff', path: '/admin/frontoffice/staff-directory', type: 'Front Office', permission: 'staff-directory:view' },
  { id: 'admin-seat-management', title: 'Seat Management', subtitle: 'Class capacity and quota allocation', path: '/admin/enrollment/seat-management', type: 'Front Office', permission: 'seat-management:view' },

  // Academic Management
  { id: 'admin-academics', title: 'Academics', subtitle: 'Class management and academic setup', path: '/admin/academics', type: 'Academic Management', permission: 'academics:view' },
  { id: 'admin-subject-management', title: 'Subject Management', subtitle: 'Configure academic subjects and electives', path: '/admin/academics/subject-management', type: 'Academic Management', permission: 'academics:view' },
  { id: 'admin-timetable', title: 'Timetable', subtitle: 'Setup class & teacher schedules', path: '/admin/academics/timetable', type: 'Academic Management', permission: 'timetable:view' },

  // Class Management
  { id: 'admin-classes', title: 'Classes', subtitle: 'Section division and class allocation', path: '/admin/class-management/classes', type: 'Class Management', permission: 'class-management:view' },
  { id: 'admin-pending-queue', title: 'Pending Queue', subtitle: 'Student section allocation queue', path: '/admin/class-management/pending-queue', type: 'Class Management', permission: 'class-management:view' },

  // Finance Management
  { id: 'admin-fees-finance', title: 'Fees & Finance', subtitle: 'Fee structures, collect payments, discounts, reports', path: '/admin/finance/fees-finance', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-head', title: 'Fee Heads', subtitle: 'Master definition of fee components', path: '/admin/finance/fees-finance/fee-head', type: 'Finance Management', permission: 'finance:fee-head:view' },
  { id: 'admin-fee-structure', title: 'Fee Structure', subtitle: 'Class fee structure configurations', path: '/admin/finance/fees-finance/fee-structure', type: 'Finance Management', permission: 'finance:fee-structure:view' },
  { id: 'admin-fee-payments', title: 'Fee Collection & Payments', subtitle: 'Record student fee payments', path: '/admin/finance/fees-finance/payments', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-discounts', title: 'Fee Discounts', subtitle: 'Manage student fee discount types', path: '/admin/finance/fees-finance/discounts', type: 'Finance Management', permission: 'finance:view' },
  { id: 'admin-fee-reports', title: 'Fee Reports', subtitle: 'Financial collection & dues reports', path: '/admin/finance/fees-finance/reports', type: 'Finance Management', permission: 'finance:view' },

  // Communication
  { id: 'admin-communication', title: 'Communication', subtitle: 'Messages, announcements, and group discussions', path: '/admin/communication', type: 'Communication', permission: 'communication:view' },
  { id: 'admin-communication-message', title: 'Message', subtitle: 'Direct messages and conversations', path: '/admin/communication/message', type: 'Communication', permission: 'communication:view' },
  { id: 'admin-communication-notices', title: 'Notices', subtitle: 'School-wide notice board', path: '/admin/communication/notices', type: 'Communication', permission: 'communication:view' },
  { id: 'admin-communication-groups', title: 'Groups', subtitle: 'Manage student & teacher groups', path: '/admin/communication/groups', type: 'Communication', permission: 'communication:view' },

  // User & Role Management
  { id: 'admin-user-management', title: 'User Management', subtitle: 'System users and account list', path: '/admin/user-management', type: 'User & Role Management', permission: 'user-management:view' },
  { id: 'admin-role-management', title: 'Role Management', subtitle: 'Configure user roles & permissions grid', path: '/admin/user-management/role-management', type: 'User & Role Management', permission: 'role-management:view' },
  { id: 'admin-departments', title: 'Departments', subtitle: 'Manage school departments', path: '/admin/user-management/departments', type: 'User & Role Management', permission: 'user-management:view' },

  // System Settings & Profile
  { id: 'admin-settings', title: 'School Settings', subtitle: 'General configuration, branding, security', path: '/admin/settings', type: 'System', permission: 'settings:view' },
  { id: 'admin-profile', title: 'Admin Profile', subtitle: 'View and update admin profile details', path: '/admin/profile', type: 'System', permission: 'profile:view' },
]

export function getTeacherBreadcrumbParts(pathname: string): [string, string] {
  if (pathname.includes('/assignments/create')) return ['Classroom Management', 'Create Assignment']
  if (pathname.includes('/assignments/review')) return ['Classroom Management', 'Review Submissions']
  if (pathname.includes('/assignments/report')) return ['Classroom Management', 'Assignment Report']
  if (pathname.includes('/assignments')) return ['Classroom Management', 'Assignments & Homework']
  if (pathname.includes('/myclasses') || pathname.includes('/classes')) return ['Classroom Management', 'My Classes']
  if (pathname.includes('/attendence') || pathname.includes('/attendance')) return ['Classroom Management', 'Attendance']
  if (pathname.includes('/lessonplans/create') || pathname.includes('/create-lesson-plan')) return ['Classroom Management', 'Create Lesson Plan']
  if (pathname.includes('/lessonplans')) return ['Classroom Management', 'Lesson Plans']
  if (pathname.includes('/examinations')) return ['Academic Management', 'Examinations & Marks']
  if (pathname.includes('/student-performance')) return ['Academic Management', 'Student Performance']
  if (pathname.includes('/leave-management')) return ['Academic Management', 'Apply Leave']
  if (pathname.includes('/announcements')) return ['Communication', 'Announcements']
  if (pathname.includes('/library')) return ['Resources', 'Library']
  return ['Main', 'Teacher Dashboard']
}

export function getAdminBreadcrumbParts(pathname: string): string[] {
  if (pathname.includes('/student-directory/add') || pathname.includes('/add-student')) return ['Front Office', 'Add Student']
  if (pathname.includes('/student-directory')) return ['Front Office', 'Student Directory']
  if (pathname.includes('/frontoffice/staff/create')) return ['Front Office', 'Create Staff']
  if (pathname.includes('/staff-directory')) return ['Front Office', 'Staff Directory']
  if (pathname.includes('/document-verification')) return ['Front Office', 'Document Verification']
  if (pathname.includes('/enrollment/seat-management')) return ['Front Office', 'Seat Management']
  if (pathname.includes('/enrollment')) return ['Front Office', 'Enrollment']
  if (pathname.includes('/admission')) return ['Front Office', 'Front Office']
  if (pathname.includes('/interview')) return ['Front Office', 'Interviews']
  if (pathname.includes('/finance/fee-structure')) return ['Finance Management', 'Fee Structure']
  if (pathname.includes('/finance')) return ['Finance Management', 'Fees & Finance']
  if (pathname.includes('/academics/subject-management')) return ['Academic Management', 'Subject Management']
  if (pathname.includes('/academics/timetable')) return ['Academic Management', 'Timetable']
  if (pathname.includes('/academics')) return ['Academic Management', 'Academics']
  if (pathname.includes('/class-management/pending-queue')) return ['Class Management', 'Pending Queue']
  if (pathname.includes('/class-management/classes')) return ['Class Management', 'Classes']
  if (pathname.includes('/academic/examinations/timetable')) return ['Examination Management', 'Exam Timetable']
  if (pathname.includes('/academic/examinations/room-management')) return ['Examination Management', 'Exam Room Management']
  if (pathname.includes('/academic/examinations/seating-rules')) return ['Examination Management', 'Seating Rules']
  if (pathname.includes('/academic/examinations/room-allocation')) return ['Examination Management', 'Room Allocation']
  if (pathname.includes('/academic/examinations/seating-plan')) return ['Examination Management', 'Seating Plan']
  if (pathname.includes('/academic/examinations/hall-ticket')) return ['Examination Management', 'Hall Ticket']
  if (pathname.includes('/academic/examinations/report-card-generation')) return ['Examination Management', 'Report Card Generation']
  if (pathname.includes('/academic/examinations')) return ['Examination Management', 'Examinations']
  if (pathname.includes('/leave-management')) return ['Leave Management', 'Leave Management']
  if (pathname.includes('/communication/view-report')) return ['Communication', 'Delivery Report']
  if (pathname.includes('/communication/message')) return ['Communication', 'Message']
  if (pathname.includes('/communication/notices')) return ['Communication', 'Notices']
  if (pathname.includes('/communication/groups')) return resolveGroupBreadcrumb(pathname)
  if (pathname.includes('/communication')) return ['Communication', 'Communication']
  if (pathname.includes('/events')) return ['Communication', 'Events']
  if (pathname.includes('/user-management/role-management')) return ['Admin', 'User Management', 'Role Management']
  if (pathname.includes('/user-management/departments')) return ['User & Role Management', 'Departments']
  if (pathname.includes('/user-management/designations')) return ['User & Role Management', 'Designations']
  if (pathname.includes('/user-management')) return ['User & Role Management', 'User Management']
  if (pathname.includes('/settings')) return ['System', 'School Settings']
  if (pathname.includes('/profile')) return ['System', 'Admin Profile']
  return ['Main', 'Dashboard']
}

function resolveGroupBreadcrumb(pathname: string): string[] {
  // Normalize path to strip leading /admin or /teacher prefix if present
  const cleanPath = pathname.replace(/^\/(admin|teacher)/, '')
  
  // Exact /communication/groups
  if (cleanPath === '/communication/groups' || cleanPath === '/communication/groups/') {
    return ['Communication', 'Groups']
  }

  // /communication/groups/create
  if (cleanPath === '/communication/groups/create') {
    return ['Communication', 'Groups', 'Create Group']
  }

  // Match /communication/groups/:groupId/:subPage
  const match = cleanPath.match(/^\/communication\/groups\/([^/]+)(?:\/(chat|members|edit|settings))?/)
  if (match) {
    const groupId = match[1]
    const subPage = match[2]
    
    // Resolve dynamic group name from storage or fallback
    let groupName = 'Group'
    try {
      const stored = window.localStorage.getItem('school-communication-groups')
      if (stored) {
        const groups = JSON.parse(stored)
        const found = groups.find((g: { id: string; name: string }) => g.id === groupId)
        if (found?.name) groupName = found.name
      }
    } catch {
      // fallback
    }

    if (groupName === 'Group') {
      if (groupId === 'jeffs-frontend-team') groupName = 'Jeffs Frontend Team'
      else if (groupId === 'jeffs-group') groupName = 'Jeffs Group'
      else if (groupId === 'grade-10-a-students') groupName = 'Class 10-A Official'
      else if (groupId === 'science-dept-teachers') groupName = 'Science Faculty'
      else groupName = groupId.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    }

    if (subPage === 'edit') {
      return ['Communication', 'Groups', 'Edit Group']
    }

    return ['Communication', 'Groups', groupName]
  }

  return ['Communication', 'Groups']
}

export function Header({
  onMenuClick,
  pageTitle,
  breadcrumb,
  userName,
  userRole,
  avatarSrc,
  hideSidebar = false,
  hideBreadcrumb = false,
}: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const authStore = useAuthStore()
  const { data: orgProfile } = useOrganizationProfile()

  const orgLogoUrl =
    orgProfile?.logoUrl ||
    orgProfile?.logo ||
    orgProfile?.logo_url ||
    orgProfile?.shortLivedLogoUrl ||
    undefined

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const { data: unreadData } = useUnreadCount()
  const { data: notificationsResponse } = useNotifications()
  const markNotificationReadMutation = useMarkNotificationRead()
  const markAllNotificationsReadMutation = useMarkAllNotificationsRead()

  const notifications = notificationsResponse?.items ?? []
  const unreadCount = unreadData?.unreadCount ?? 0

  const markAsRead = (id: string, actionUrl?: string | null) => {
    markNotificationReadMutation.mutate(id)
    if (actionUrl) {
      navigate(actionUrl)
    }
  }

  const markAllAsRead = () => {
    markAllNotificationsReadMutation.mutate()
  }

  const isTeacherView = useMemo(() => location.pathname.startsWith('/teacher'), [location.pathname])

  const [recentSearches, setRecentSearches] = useState<string[]>(() =>
    isTeacherView
      ? ['Assignments & Homework', 'Attendance', 'Lesson Plans']
      : ['Student Directory', 'Create Staff / Teacher', 'Fees & Finance', 'Add New Student']
  )

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLElement | null>(null)
  const profileRef = useRef<HTMLDivElement | null>(null)

  // Debounce search query for silky smooth typing responsiveness
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 150)
    return () => clearTimeout(timer)
  }, [query])

  // Select active search dataset based on user workspace and RBAC permissions
  const searchItems = useMemo(() => {
    const rawItems = isTeacherView ? teacherSearchItems : adminSearchItems
    return rawItems.filter((item) => {
      // System admin or wildcard permission access
      if (authStore.role === 'admin' || authStore.hasPermission('*')) {
        return true
      }
      // Permission-based access check
      if (item.permission) {
        if (authStore.hasPermission(item.permission)) return true
        if (authStore.hasPermission('finance:view') && item.permission.startsWith('finance:')) return true
        return false
      }
      return true
    })
  }, [isTeacherView, authStore])

  const normalizedQuery = debouncedQuery.trim().toLowerCase()

  const filteredResults = useMemo(() => {
    if (!normalizedQuery) {
      return searchItems.slice(0, 8)
    }

    return searchItems.filter((item) =>
      [item.title, item.subtitle, item.type].some((value) =>
        value.toLowerCase().includes(normalizedQuery)
      )
    )
  }, [normalizedQuery, searchItems])

  // Dynamic user details resolution
  const resolvedUserName = useMemo(() => {
    if (userName) return userName
    if (authStore.user?.name) return authStore.user.name
    const storedUser = window.localStorage.getItem('school-erp-user-name')
    if (storedUser) return storedUser
    if (location.pathname.startsWith('/superadmin')) return 'John Mathew'
    if (location.pathname.startsWith('/admin')) return 'Anushka'
    return 'Rahul Sharma'
  }, [userName, authStore.user, location.pathname])

  const resolvedUserRole = useMemo(() => {
    if (userRole) return userRole
    const currentRole = authStore.role as string | null
    if (currentRole === 'fee_clerk') return 'Fee Clerk'
    if (currentRole === 'finance_head_manager') return 'Fee Manager'
    if (currentRole === 'teacher' || currentRole === 'staff' || currentRole === 'STAFF') return 'Staff'
    if (currentRole === 'student') return 'Student'
    if (currentRole === 'admin') return 'Admin'
    if (currentRole === 'hr') return 'HR Manager'
    if (currentRole === 'superadmin') return 'Super Admin'

    if (location.pathname.startsWith('/superadmin')) return 'Super Admin'
    if (location.pathname.startsWith('/teacher')) return 'Staff'
    if (location.pathname.startsWith('/student')) return 'Student'
    if (location.pathname.startsWith('/hr')) return 'HR Manager'
    if (location.pathname.startsWith('/admin')) return 'Admin'

    return 'Staff'
  }, [userRole, authStore.role, location.pathname])

  // Breadcrumb resolution
  const parsedBreadcrumb = useMemo<{ parents: string[]; current: string }>(() => {
    let rawParts: string[] = []

    if (Array.isArray(breadcrumb)) {
      rawParts = breadcrumb.filter(Boolean)
    } else if (typeof breadcrumb === 'string' && breadcrumb.trim()) {
      rawParts = breadcrumb.split(' / ').map((p) => p.trim()).filter(Boolean)
    } else {
      const path = location.pathname
      if (path.startsWith('/teacher')) {
        rawParts = getTeacherBreadcrumbParts(path)
      } else if (path.startsWith('/admin')) {
        rawParts = getAdminBreadcrumbParts(path)
      } else {
        rawParts = ['Dashboard', pageTitle || 'Overview']
      }
    }

    if (rawParts.length > 0) {
      const lastItem = rawParts[rawParts.length - 1]
      if (['Student', 'Teacher', 'School Admin', 'Admin', 'HR Manager', 'Super Admin'].includes(lastItem)) {
        rawParts.pop()
      }
    }

    if (rawParts.length === 0) {
      return { parents: [], current: pageTitle || 'Dashboard' }
    }

    if (rawParts.length === 1) {
      return { parents: [], current: rawParts[0] }
    }

    return {
      parents: rawParts.slice(0, -1),
      current: rawParts[rawParts.length - 1],
    }
  }, [breadcrumb, location.pathname, pageTitle])

  const prevPathRef = useRef(location.pathname)
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname
      setPanelOpen(false)
      setNotificationsOpen(false)
      setProfileDropdownOpen(false)
    }
  }, [location.pathname])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        setPanelOpen(false)
        setNotificationsOpen(false)
      }

      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false)
      }
    }

    const handleScroll = () => {
      setPanelOpen(false)
      setProfileDropdownOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleSettingsNavigation = () => {
    const isTeacher = authStore.role === 'teacher' || location.pathname.startsWith('/teacher')
    if (isTeacher) {
      navigate('/teacher/settings')
    } else {
      navigate('/settings')
    }
  }

  const handleProfileNavigation = () => {
    setProfileDropdownOpen(false)
    const isTeacher = authStore.role === 'teacher' || location.pathname.startsWith('/teacher')
    if (isTeacher) {
      navigate('/teacher/profile')
    } else {
      navigate('/admin/profile')
    }
  }

  return (
    <header
      ref={headerRef}
      className={`fixed left-0 right-0 top-0 z-40 border-b border-[#e8edf3] bg-white shadow-[0_6px_20px_rgba(15,23,42,0.04)] ${
        hideSidebar ? 'lg:left-0' : 'lg:left-[240px]'
      } school-admin-topbar font-['Inter']`}
    >
      <div className="flex h-full items-center justify-between gap-4 px-4 py-3 sm:px-5 lg:px-5 relative">
        {/* Left Side: Menu button + Dynamic Breadcrumb Navigation */}
        <div className="flex items-center gap-3 min-w-0">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#d7e3e5] bg-white text-[#2b5560] shadow-sm lg:hidden shrink-0"
              aria-label="Open sidebar"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
          )}

          {/* Dynamic Breadcrumb Hierarchy — hidden on full-screen pages like group chat */}
          {!hideBreadcrumb && (
            <div className="flex items-center gap-1.5 text-[14px] leading-tight min-w-0 truncate">
              {parsedBreadcrumb.parents.map((parent, idx) => (
                <span key={`${parent}-${idx}`} className="flex items-center gap-1.5 shrink-0">
                  <span className="font-normal text-slate-500 truncate">{parent}</span>
                  <span className="text-slate-300 font-medium">/</span>
                </span>
              ))}
              <span className="font-bold text-black truncate">
                {parsedBreadcrumb.current}
              </span>
            </div>
          )}
        </div>

        {/* Center & Right Side Container */}
        <div className="flex flex-1 items-center justify-end gap-4">
          {/* Center: Contextual Search Input */}
          <div ref={wrapperRef} className="relative hidden w-full max-w-[220px] lg:max-w-[280px] xl:max-w-[340px] md:block">
            <SearchInput
              placeholder="Search"
              className="w-full"
              variant="header"
              inputClassName="school-admin-global-search"
              value={query}
              onChange={(value) => {
                setQuery(value)
                setPanelOpen(true)
              }}
              onFocus={() => setPanelOpen(true)}
            />

            {panelOpen && (
              <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-full min-w-[340px] max-w-[440px] overflow-hidden rounded-[16px] border border-[#b7dfd9] bg-[#f0f9f8] p-2 shadow-[0_16px_36px_rgba(15,23,42,0.12)] font-['Inter']">
                {query.trim() ? (
                  <div className="space-y-1">
                    <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#146971]">
                      Search Results ({filteredResults.length})
                    </div>
                    {filteredResults.length > 0 ? (
                      filteredResults.map((result) => (
                        <button
                          key={result.id}
                          type="button"
                          onClick={() => {
                            if (!recentSearches.includes(result.title)) {
                              setRecentSearches((prev) => [result.title, ...prev.slice(0, 4)])
                            }
                            navigate(result.path)
                            setPanelOpen(false)
                          }}
                          className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition bg-transparent hover:bg-[#d8eee9] cursor-pointer group"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <SearchIcon className="h-4 w-4 text-[#146971] shrink-0" />
                            <span className="text-[13px] font-semibold text-slate-800 truncate">
                              {result.title}
                            </span>
                          </div>
                          <span className="text-[11px] font-medium text-[#146971] bg-white px-2 py-0.5 rounded-md border border-[#c3e5df] shrink-0">
                            {result.type}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-center text-[13px] text-slate-500">
                        No pages or actions found matching &quot;{query.trim()}&quot;
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#146971]/80">
                      Recent Searches
                    </div>

                    {recentSearches.length > 0 ? (
                      recentSearches.map((item) => (
                        <div
                          key={item}
                          onClick={() => {
                            setQuery(item)
                            const match = searchItems.find((i) => i.title.toLowerCase() === item.toLowerCase())
                            if (match) {
                              navigate(match.path)
                              setPanelOpen(false)
                            }
                          }}
                          className="group flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition bg-transparent hover:bg-[#d8eee9] cursor-pointer"
                        >
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <Clock className="h-3.5 w-3.5 text-slate-500 group-hover:text-[#146971] shrink-0 transition-colors" />
                            <span className="text-[13px] font-medium text-slate-700 group-hover:text-slate-900 truncate">
                              {item}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setRecentSearches((prev) => prev.filter((r) => r !== item))
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-[#c3e5df] rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            title="Remove recent search"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-center text-[13px] text-slate-500">
                        No recent searches
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Side: Notifications, Settings, User Info & Avatar */}
          <div className="flex items-center gap-2 sm:gap-3 text-[#070707]">
            {/* Bell Icon with Red Dot Badge */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setNotificationsOpen((prev) => !prev)}
                className="relative inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-slate-100 cursor-pointer"
                aria-label="Open notifications"
              >
                <Bell className="h-5 w-5 text-slate-600 stroke-[1.75]" />
                {unreadCount > 0 && (
                  <span className="absolute right-[8px] top-[7px] h-2.5 w-2.5 rounded-full bg-[#ff3b3b] ring-2 ring-white" />
                )}
              </button>

              <NotificationPanel
                open={notificationsOpen}
                onClose={() => setNotificationsOpen(false)}
                notifications={notifications}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
              />
            </div>

            {/* Settings Icon (Role-based Navigation) */}
            <button
              type="button"
              onClick={handleSettingsNavigation}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-slate-100 cursor-pointer"
              aria-label="Settings"
            >
              <Settings className="h-5 w-5 text-slate-600 stroke-[1.75]" />
            </button>

            <div className="h-8 w-px bg-slate-200" />

            {/* User Profile Container & Dropdown Trigger */}
            <div ref={profileRef} className="relative">
              <div
                onClick={() => setProfileDropdownOpen((prev) => !prev)}
                className="flex items-center gap-3 cursor-pointer p-1 rounded-xl hover:bg-slate-100/70 transition"
              >
                <div className="text-right hidden sm:block shrink-0 min-w-max">
                  <p
                    style={{ color: '#191C1D' }}
                    className="school-admin-user-role font-['Inter'] text-[14px] font-bold leading-tight whitespace-nowrap"
                  >
                    {resolvedUserRole === 'School Admin' ? 'Admin' : resolvedUserRole}
                  </p>
                  <p
                    style={{ color: '#3F484A' }}
                    className="school-admin-user-name font-['Inter'] text-[12px] font-medium leading-tight mt-0.5 whitespace-nowrap"
                  >
                    {resolvedUserName}
                  </p>
                </div>

                <Avatar
                  name={resolvedUserName}
                  src={orgLogoUrl || avatarSrc || (authStore.user as any)?.avatarUrl || (authStore.user as any)?.avatar || undefined}
                  size="sm"
                  className="!h-9 !w-9 object-cover rounded-full shrink-0 shadow-xs border border-slate-200"
                />
              </div>

              {/* PROFILE DROPDOWN MENU */}
              {profileDropdownOpen && (
                <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-52 overflow-hidden rounded-[18px] border border-[#d2e5e2] bg-white p-2 shadow-[0_12px_32px_rgba(15,23,42,0.12)] animate-in fade-in zoom-in-95 duration-150 font-['Inter']">
                  <div className="space-y-1">
                    <button
                      type="button"
                      onClick={handleProfileNavigation}
                      className="flex w-full items-center gap-3 rounded-[12px] bg-transparent px-4 py-2.5 text-[14px] font-medium text-slate-700 transition cursor-pointer hover:bg-[#bfe6e2] hover:text-[#146971] group"
                    >
                      <User className="h-5 w-5 text-slate-600 transition-colors group-hover:text-[#146971] shrink-0" />
                      <span>View profile</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setProfileDropdownOpen(false)
                        setShowLogoutModal(true)
                      }}
                      className="flex w-full items-center gap-3 rounded-[12px] bg-transparent px-4 py-2.5 text-[14px] font-medium text-slate-700 transition cursor-pointer hover:bg-[#bfe6e2] hover:text-[#146971] group"
                    >
                      <LogOut className="h-5 w-5 text-slate-600 transition-colors group-hover:text-[#146971] shrink-0" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <LogoutConfirmModal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false)
          authStore.logout()
          navigate('/login')
        }}
      />
    </header>
  )
}

export default Header
